import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Chip,
  Stack,
  Paper,
  Grid,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import { createUserProfile, updateUserProfile } from '../../graphql/mutations';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const FAVORITE_STYLE_OPTIONS = [
  'Old School Dancehall',
  'Middle School Dancehall',
  'New School Dancehall',
  'Dance Fitness',
  'Dancehall Queen Style',
];

const GOAL_OPTIONS = [
  'Learn the basics',
  'Get fitter with dance',
  'Build confidence',
  'Become a pro dancer',
  'Train for performance',
  'Just dance for fun',
];

const TRAINING_OPTIONS = [
  '1–2 times per week',
  '3–4 times per week',
  'Everyday',
  'Just vibes, no schedule',
];

const HOW_HEARD_OPTIONS = [
  'TikTok',
  'Instagram',
  'Facebook',
  'YouTube',
  'WhatsApp',
  'Google Search',
  'Friend or family',
  'Dance class / community',
  'Saw your dance videos',
  'Other',
];

const TOTAL_STEPS = 4; // steps 0–3

const OnboardingWizard = ({ user, existingProfile, onComplete }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    displayName: existingProfile?.displayName || user?.username || '',
    country: existingProfile?.country || '',
    danceLevel: existingProfile?.danceLevel || '',
    danceExperienceYears: existingProfile?.danceExperienceYears ?? '',
    danceExperienceMonths: existingProfile?.danceExperienceMonths ?? '',
    favoriteStyles: existingProfile?.favoriteStyles || [],
    trainingFrequency: existingProfile?.trainingFrequency || '',
    goals: existingProfile?.goals || '',
    howHeardAboutUs: existingProfile?.howHeardAboutUs || '',
    howHeardAboutUsOther: '',
  });

  const next = () => {
    if (step < TOTAL_STEPS - 1) setStep(step + 1);
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleToggleStyle = (style) => {
    const selected = form.favoriteStyles;
    if (selected.includes(style)) {
      setForm({ ...form, favoriteStyles: selected.filter((s) => s !== style) });
    } else {
      setForm({ ...form, favoriteStyles: [...selected, style] });
    }
  };

  const handleSelectGoal = (goal) => {
    setForm({ ...form, goals: goal });
  };

  const handleSelectTraining = (option) => {
    setForm({ ...form, trainingFrequency: option });
  };

  const handleSelectHowHeard = (option) => {
    setForm({
      ...form,
      howHeardAboutUs: option,
      howHeardAboutUsOther: option === 'Other' ? form.howHeardAboutUsOther : '',
    });
  };

  const canNext = () => {
    // basic validation per step
    if (step === 0) {
      // require at least name; country is optional but recommended
      return form.displayName.trim().length > 0;
    }
    if (step === 1) {
      // require dance level
      return !!form.danceLevel;
    }
    // other steps optional
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      const sub = user?.attributes?.sub;
      const username =
        user?.username ||
        user?.attributes?.email ||
        user?.attributes?.phone_number ||
        '';

      if (!sub) {
        throw new Error('Missing user.sub from Cognito user attributes.');
      }

      const input = {
        id: sub, // tie profile to Cognito user
        ...(!existingProfile && { username }),
        onboardingCompleted: true,
        displayName: form.displayName,
        country: form.country || null,
        danceLevel: form.danceLevel,
        danceExperienceYears: form.danceExperienceYears
          ? Number(form.danceExperienceYears)
          : null,
        danceExperienceMonths: form.danceExperienceMonths
          ? Number(form.danceExperienceMonths)
          : null,
        favoriteStyles: form.favoriteStyles,
        trainingFrequency: form.trainingFrequency || null,
        goals: form.goals || null,
        howHeardAboutUs:
          form.howHeardAboutUs === 'Other' && form.howHeardAboutUsOther
            ? form.howHeardAboutUsOther
            : form.howHeardAboutUs || null,
      };

      const mutation = existingProfile ? updateUserProfile : createUserProfile;
      const resultKey = existingProfile ? 'updateUserProfile' : 'createUserProfile';
      const res = await API.graphql(graphqlOperation(mutation, { input }));

      const profile = res?.data?.[resultKey];
      if (!profile) {
        throw new Error('No profile returned after saving onboarding.');
      }

      if (onComplete) onComplete(profile);
    } catch (err) {
      console.error('Error saving onboarding profile (raw):', err);

      if (Array.isArray(err?.errors) && err.errors.length > 0) {
        err.errors.forEach((e) => console.error('GraphQL error:', e.message));
        setError(err.errors[0].message);
      } else if (Array.isArray(err?.data?.errors) && err.data.errors.length > 0) {
        err.data.errors.forEach((e) => console.error('GraphQL error:', e.message));
        setError(err.data.errors[0].message);
      } else {
        console.error('Error (stringified):', JSON.stringify(err, null, 2));
        setError('Failed to save your profile. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  const renderStep = () => {
    switch (step) {
      // STEP 1 – Basic info
      case 0:
        return (
          <Box>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Welcome to JA Legends Dance
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
              Let’s start with a few basic details so we can personalise your experience.
            </Typography>

            <TextField
              label="Display Name"
              fullWidth
              sx={{ mb: 3 }}
              value={form.displayName}
              onChange={(e) =>
                setForm({ ...form, displayName: e.target.value })
              }
              InputLabelProps={{ style: { color: '#e5e7eb' } }}
              InputProps={{ style: { color: 'white' } }}
            />

            <TextField
              label="Country / Location"
              placeholder="Example: Jamaica, United States, UK, Canada..."
              fullWidth
              value={form.country}
              onChange={(e) =>
                setForm({ ...form, country: e.target.value })
              }
              InputLabelProps={{ style: { color: '#e5e7eb' } }}
              InputProps={{ style: { color: 'white' } }}
            />
          </Box>
        );

      // STEP 2 – Dance level + experience
      case 1:
        return (
          <Box>
            <Typography variant="h5" fontWeight="bold" mb={2}>
              Your dance level and experience
            </Typography>
            <Typography variant="body1" mb={3}>
              Choose your current level and tell us how long you have been dancing.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <LevelCard
                  label="Beginner"
                  description="I am just getting started and want to learn the basics."
                  selected={form.danceLevel === 'beginner'}
                  onClick={() => setForm({ ...form, danceLevel: 'beginner' })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LevelCard
                  label="Intermediate"
                  description="I have learned choreography before and can follow along."
                  selected={form.danceLevel === 'intermediate'}
                  onClick={() =>
                    setForm({ ...form, danceLevel: 'intermediate' })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LevelCard
                  label="Advanced"
                  description="I am an experienced dancer and ready for a challenge."
                  selected={form.danceLevel === 'advanced'}
                  onClick={() =>
                    setForm({ ...form, danceLevel: 'advanced' })
                  }
                />
              </Grid>
            </Grid>

            <Typography variant="body1" mb={2}>
              How long have you been dancing?
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Years"
                  type="number"
                  fullWidth
                  value={form.danceExperienceYears}
                  onChange={(e) =>
                    setForm({ ...form, danceExperienceYears: e.target.value })
                  }
                  InputProps={{ style: { color: 'white' } }}
                  InputLabelProps={{ style: { color: '#e5e7eb' } }}
                  inputProps={{ min: 0, max: 80 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Months"
                  type="number"
                  fullWidth
                  value={form.danceExperienceMonths}
                  onChange={(e) =>
                    setForm({ ...form, danceExperienceMonths: e.target.value })
                  }
                  InputProps={{ style: { color: 'white' } }}
                  InputLabelProps={{ style: { color: '#e5e7eb' } }}
                  inputProps={{ min: 0, max: 11 }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      // STEP 3 – Favourite styles + routine
      case 2:
        return (
          <Box>
            <Typography variant="h5" fontWeight="bold" mb={2}>
              Your favourite styles and routine
            </Typography>
            <Typography variant="body1" mb={3}>
              Choose the styles you love and how often you plan to train.
            </Typography>

            <Typography variant="subtitle1" fontWeight="600" mb={1}>
              Favourite styles
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} mb={3}>
              {FAVORITE_STYLE_OPTIONS.map((style) => {
                const selected = form.favoriteStyles.includes(style);
                return (
                  <Chip
                    key={style}
                    label={style}
                    clickable
                    onClick={() => handleToggleStyle(style)}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: selected ? 700 : 400,
                      px: 1.5,
                      py: 1,
                      fontSize: '0.9rem',
                      bgcolor: selected ? '#facc15' : 'rgba(15, 23, 42, 0.85)',
                      color: selected ? '#111827' : '#e5e7eb',
                      borderColor: selected ? '#facc15' : '#4b5563',
                    }}
                  />
                );
              })}
            </Stack>

            <Typography variant="subtitle1" fontWeight="600" mb={1}>
              How often do you plan to dance?
            </Typography>
            <Stack spacing={1}>
              {TRAINING_OPTIONS.map((option) => {
                const selected = form.trainingFrequency === option;
                return (
                  <Button
                    key={option}
                    onClick={() => handleSelectTraining(option)}
                    variant={selected ? 'contained' : 'outlined'}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      borderRadius: 2,
                      borderColor: selected ? '#22c55e' : '#4b5563',
                      backgroundColor: selected ? '#22c55e' : 'transparent',
                      color: selected ? '#0b1120' : '#e5e7eb',
                    }}
                  >
                    {option}
                  </Button>
                );
              })}
            </Stack>
          </Box>
        );

      // STEP 4 – Goals + How did you hear about us
      case 3:
        return (
          <Box>
            <Typography variant="h5" fontWeight="bold" mb={2}>
              Your goals and how you found us
            </Typography>

            <Typography variant="subtitle1" fontWeight="600" mb={1.5}>
              What is your main goal with JA Legends Dance?
            </Typography>
            <Stack spacing={1} mb={3}>
              {GOAL_OPTIONS.map((goal) => {
                const selected = form.goals === goal;
                return (
                  <Button
                    key={goal}
                    onClick={() => handleSelectGoal(goal)}
                    variant={selected ? 'contained' : 'outlined'}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      borderRadius: 2,
                      borderColor: selected ? '#facc15' : '#4b5563',
                      backgroundColor: selected ? '#facc15' : 'transparent',
                      color: selected ? '#111827' : '#e5e7eb',
                    }}
                  >
                    {goal}
                  </Button>
                );
              })}
            </Stack>

            <Typography variant="body2" mb={2}>
              Or describe it in your own words:
            </Typography>
            <TextField
              placeholder="Example: I want to connect more with my culture through dance."
              fullWidth
              multiline
              minRows={2}
              value={
                GOAL_OPTIONS.includes(form.goals) ? '' : (form.goals || '')
              }
              onChange={(e) =>
                setForm({ ...form, goals: e.target.value })
              }
              InputLabelProps={{ style: { color: '#e5e7eb' } }}
              InputProps={{ style: { color: 'white' } }}
              sx={{ mb: 4 }}
            />

            <Typography variant="subtitle1" fontWeight="600" mb={1.5}>
              How did you hear about JA Legends Dance?
            </Typography>
            <Stack spacing={1} mb={3}>
              {HOW_HEARD_OPTIONS.map((option) => {
                const selected = form.howHeardAboutUs === option;
                return (
                  <Button
                    key={option}
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => handleSelectHowHeard(option)}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      borderRadius: 2,
                      borderColor: selected ? '#3b82f6' : '#4b5563',
                      backgroundColor: selected ? '#3b82f6' : 'transparent',
                      color: selected ? '#ffffff' : '#e5e7eb',
                    }}
                  >
                    {option}
                  </Button>
                );
              })}
            </Stack>

            {form.howHeardAboutUs === 'Other' && (
              <TextField
                placeholder="Tell us where you heard about us"
                fullWidth
                multiline
                minRows={2}
                value={form.howHeardAboutUsOther}
                onChange={(e) =>
                  setForm({
                    ...form,
                    howHeardAboutUsOther: e.target.value,
                  })
                }
                InputLabelProps={{ style: { color: '#e5e7eb' } }}
                InputProps={{ style: { color: 'white' } }}
              />
            )}

            <Typography variant="body2" mt={3}>
              When you click <strong>Finish</strong>, we will save your profile
              and take you into the JA Legends platform.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Box
        py={6}
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            width: '100%',
            maxWidth: 900,
            margin: '0 auto',
            background:
              'linear-gradient(135deg, #020617 0%, #111827 40%, #0f172a 70%, #b91c1c 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Accent circle */}
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, #facc15 0%, #b91c1c 60%, transparent 70%)',
              top: -40,
              right: -40,
              opacity: 0.35,
              pointerEvents: 'none',
            }}
          />

          {/* Top bar with back + step indicator */}
          <Box
            mb={3}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              {step > 0 ? (
                <IconButton
                  onClick={back}
                  sx={{
                    color: 'white',
                    border: '1px solid rgba(148, 163, 184, 0.7)',
                  }}
                  size="small"
                >
                  <ArrowBackIcon />
                </IconButton>
              ) : (
                <Box sx={{ width: 40 }} />
              )}
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 2 }}>
                  JA LEGENDS ONBOARDING
                </Typography>
                <Typography variant="body2">
                  Step {step + 1} of {TOTAL_STEPS}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ width: 140 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    background:
                      'linear-gradient(90deg, #22c55e, #facc15, #ef4444)',
                  },
                }}
              />
            </Box>
          </Box>

          <Box mt={1} mb={3}>
            {renderStep()}
          </Box>

          {error && (
            <Typography color="error" sx={{ mt: 1, mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Bottom buttons */}
          <Box
            mt={4}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Built with love for the global dance community.
            </Typography>

            {step < TOTAL_STEPS - 1 ? (
              <Button
                variant="contained"
                onClick={next}
                disabled={!canNext()}
                sx={{
                  px: 4,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 600,
                  background:
                    'linear-gradient(90deg, #22c55e, #facc15, #f97316)',
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving || !form.danceLevel || !form.displayName}
                sx={{
                  px: 4,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                  background:
                    'linear-gradient(90deg, #22c55e, #facc15, #ef4444)',
                }}
              >
                {saving ? 'Saving…' : 'Finish & Continue'}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

const LevelCard = ({ label, description, selected, onClick }) => {
  return (
    <Paper
      onClick={onClick}
      elevation={selected ? 8 : 3}
      sx={{
        p: 2,
        borderRadius: 3,
        cursor: 'pointer',
        height: '100%',
        border: selected ? '2px solid #facc15' : '1px solid #4b5563',
        background: selected
          ? 'linear-gradient(135deg, #facc15, #f97316)'
          : 'rgba(15, 23, 42, 0.9)',
        color: selected ? '#111827' : '#e5e7eb',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: selected
            ? '0 0 20px rgba(250, 204, 21, 0.5)'
            : '0 0 15px rgba(15, 23, 42, 0.8)',
        },
      }}
    >
      <Typography variant="subtitle1" fontWeight="700" mb={1}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.95 }}>
        {description}
      </Typography>
    </Paper>
  );
};

export default OnboardingWizard;
